import * as React from 'react'
import { Box, Paper, Typography } from '@mui/material'
import styled from '@emotion/styled'
import { useAtomValue } from 'infrastructure/state/jotai.wrapper'
import {
  useSyncStandards,
  useSyncCompletion,
  useSyncResultsSummary,
} from './hooks'
import { SummaryTable } from '../../SummaryTable'
import { DisplaySyncDifferences } from './DisplaySyncDifferences'
import { OpenJsonButton } from '../../utility/OpenJsonButton'
import { DownloadJsonButton } from '../../utility/DownloadJsonButton'
import { StartAndMonitorFlow } from '../../contentService/FlowStartandMonitor'
import {
  syncResultsAtom,
  syncErrorsAtom,
  refreshCounterAtom,
  syncDifferencesAtom,
} from './atoms'

const SectionPaper = styled(Paper)(() => ({
  padding: '24px',
  marginBottom: '24px',
}))

interface SyncTreeProps {
  treeId: string
  versionId: string
}

export function SyncTree({ treeId, versionId }: SyncTreeProps) {
  const syncDifferences = useAtomValue(syncDifferencesAtom)
  const { syncStandards } = useSyncStandards(treeId, versionId, syncDifferences)
  const handleSyncComplete = useSyncCompletion()
  const syncResults = useAtomValue(syncResultsAtom)
  const syncErrors = useAtomValue(syncErrorsAtom)
  const refreshCounter = useAtomValue(refreshCounterAtom)
  const syncStarted = refreshCounter > 0

  const { syncResultsRows, totalStandardsCreated } = useSyncResultsSummary()

  return (
    <>
      <SectionPaper>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h6">Synchronize Tree</Typography>
          {syncDifferences.length > 0 && (
            <>
              <OpenJsonButton
                data={{
                  tree_id: treeId,
                  version_id: versionId,
                  differences: syncDifferences,
                }}
                disabled={syncDifferences.length === 0}
                buttonText="Open JSON"
              />
              <DownloadJsonButton
                data={{
                  tree_id: treeId,
                  version_id: versionId,
                  differences: syncDifferences,
                }}
                filename={`sync_differences_${treeId}_${versionId}.json`}
                disabled={syncDifferences.length === 0}
              />
            </>
          )}
        </Box>
        <DisplaySyncDifferences treeId={treeId} versionId={versionId} />
        <Box>
          <Box sx={{ mt: 2 }}>
            <StartAndMonitorFlow
              resetTrigger={refreshCounter}
              disabled={syncDifferences.length === 0 || syncStarted}
              buttonText="Apply Changes"
              processingText="Applying skills list changes..."
              onFlowComplete={handleSyncComplete}
              startFlowFunction={syncStandards}
              downloadResults={true}
            />
          </Box>
          {syncResults.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Sync Results
              </Typography>
              <SummaryTable
                headers={['Skills list ID', 'Standards synchronized']}
                rows={syncResultsRows}
              />
              <Typography variant="body2" sx={{ mt: 2 }}>
                Total standards synchronized: {totalStandardsCreated}
              </Typography>
            </Box>
          )}
          {syncErrors.length > 0 && (
            <Box sx={{ mt: 3 }}>
              <Typography variant="h6" gutterBottom color="error">
                Errors
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                {syncErrors.map((error, index) => (
                  <Typography
                    key={index}
                    component="li"
                    variant="body2"
                    color="error"
                  >
                    {error}
                  </Typography>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      </SectionPaper>
    </>
  )
}
